import { DownloadSimpleIcon, FilesIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { captureException } from '@sentry/react';

import { BaseButton } from '../../../components/button/BaseButton';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { useTranslation } from '../../../contexts/translation-context';
import { downloadUrl } from '../../../utils/download';
import { getDisplayError } from '../../../utils/get-display-error';
import {
  DOCUMENT_DIALOG_TITLE_CLASS,
  DocumentDialogFooter,
  DocumentFileRow,
} from '../../document/components/DocumentDialogParts';
import UploadForm from '../../document/components/UploadForm';
import { useDownloadDocument } from '../../document/hooks/useDownloadDocument';
import { OrderDocumentType, OrderStatus } from 'src/types/enums';
import type { OrderDocumentTypeValue } from '../endpoints/shared.types';
import { linkOrderDocument, unlinkOrderDocument } from '../models';
import type { Order } from '../pages/order/Order';
import { useOrder } from '../pages/order/orderContext';
import { OrderDocumentTypeToTranslationKey } from '../utils/document-validation';

export const CMR_BUNDLE_DOC_TYPES: Array<{ type: OrderDocumentType; namePrefix: string }> = [
  { type: OrderDocumentType.Cmr, namePrefix: 'CMR' },
  { type: OrderDocumentType.WeighSlip, namePrefix: 'Weegbon' },
  { type: OrderDocumentType.DeliveryNote, namePrefix: 'Leveringsbon' },
  { type: OrderDocumentType.PalletNote, namePrefix: 'Palletbon' },
  { type: OrderDocumentType.AnnexII, namePrefix: 'Annex II' },
  { type: OrderDocumentType.T1Document, namePrefix: 'T1-document' },
  { type: OrderDocumentType.AnnexVII, namePrefix: 'Annex VII' },
];

export function getCmrBundleUrl(orderId: string): string {
  return `/api/v1/orders/${orderId}/cmr-bundle.pdf`;
}

export function hasCmrBundleDocuments(order: Order): boolean {
  return order.documents.some((d) => CMR_BUNDLE_DOC_TYPES.some((t) => t.type === d.type));
}

interface Props {
  order: Order;
  /** Custom element that opens the dialog instead of the default button. */
  trigger?: React.ReactElement;
}

/**
 * Dialog around the CMR bundle: open the merged PDF, and open, upload, replace
 * or delete each part (CMR, weigh slip, …) on its own row.
 */
export const OrderCmrBundleDialog: React.FC<Props> = ({ order, trigger }) => {
  const { refreshData } = useOrder();
  const { i18n } = useTranslation();
  const { downloadFile, downloadingFiles } = useDownloadDocument();
  const [open, setOpen] = useState(false);
  const [uploadType, setUploadType] = useState<OrderDocumentType | null>(null);

  // CMR is implicitly always required (matches cmr-bundle.server.ts).
  const requiredTypes = new Set<OrderDocumentType>([
    OrderDocumentType.Cmr,
    ...((order.customer?.requiredDocuments ?? []) as OrderDocumentType[]),
  ]);
  const hasBundle = hasCmrBundleDocuments(order);
  const disableUpload = order.status === OrderStatus.Cancelled;

  const openBundle = () => {
    downloadUrl(getCmrBundleUrl(order.id), { openInNewTab: true });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setUploadType(null);
      }}
    >
      <Dialog.Trigger asChild>
        {trigger ?? (
          <BaseButton
            color={hasBundle ? 'primary' : 'default-outline'}
            iconLeft={<FilesIcon className="button-icon" />}
          >
            CMR
          </BaseButton>
        )}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className={DOCUMENT_DIALOG_TITLE_CLASS}>CMR-bundel</Dialog.Title>

          <DocumentFileRow
            icon={FilesIcon}
            name="CMR-bundel"
            meta={hasBundle ? 'Alle vervoersdocumenten als één PDF' : 'Nog geen vervoersdocumenten ontvangen'}
            muted={!hasBundle}
            actions={
              <BaseButton
                size={6}
                iconLeft={<DownloadSimpleIcon className="h-3 w-3" />}
                onClick={openBundle}
                disabled={!hasBundle}
              >
                Openen
              </BaseButton>
            }
          />

          <div className="label-text mb-2 mt-4">Onderdelen</div>
          <div className="flex flex-col gap-2">
            {CMR_BUNDLE_DOC_TYPES.map(({ type, namePrefix }) => {
              const file = order.documents.find((d) => d.type === type)?.document;
              const name = i18n(OrderDocumentTypeToTranslationKey[type]);
              const isUploading = uploadType === type;
              return (
                <div key={type}>
                  <DocumentFileRow
                    name={name}
                    meta={file ? file.name : requiredTypes.has(type) ? 'Ontbreekt' : 'Niet vereist'}
                    muted={!file}
                    actions={
                      <>
                        {file && (
                          <BaseButton
                            size={6}
                            iconLeft={<DownloadSimpleIcon className="h-3 w-3" />}
                            onClick={() => downloadFile(file)}
                            isLoading={downloadingFiles.includes(file.id)}
                          >
                            Openen
                          </BaseButton>
                        )}
                        {!disableUpload && (
                          <BaseButton
                            size={6}
                            color={file || isUploading ? 'ghost' : 'secondary'}
                            iconLeft={<PlusIcon className="h-3 w-3" />}
                            onClick={() => setUploadType(isUploading ? null : type)}
                          >
                            {file ? 'Vervangen' : 'Uploaden'}
                          </BaseButton>
                        )}
                        {file && (
                          <ConfirmDialog
                            triggerSize={6}
                            triggerColor="danger"
                            triggerTitle="Verwijder document"
                            triggerText={<TrashIcon className="h-3 w-3" />}
                            title="Ben je zeker dat je dit document wilt verwijderen?"
                            submitText="Verwijder document"
                            description="Ben je zeker dat je dit document wilt verwijderen?"
                            onSubmit={async () => {
                              try {
                                await unlinkOrderDocument(order.id, {
                                  documentId: file.id,
                                  type: type as OrderDocumentTypeValue,
                                });
                                toast.success('Bestand gewist');
                                refreshData();
                              } catch (err) {
                                captureException(err);
                                toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                                throw err;
                              }
                            }}
                          />
                        )}
                      </>
                    }
                  />
                  {isUploading && (
                    <div className="mt-2 rounded-lg border border-border p-3">
                      <div className="label-text mb-2">{file ? `${name} vervangen` : `${name} uploaden`}</div>
                      <UploadForm
                        initialName={`${namePrefix} ${order.orderNumber ?? ''}`}
                        onSubmit={async (document) => {
                          try {
                            await linkOrderDocument(order.id, {
                              documentId: document.id,
                              type: type as OrderDocumentTypeValue,
                            });
                            toast.success('Bestand opgeladen');
                            setUploadType(null);
                            refreshData();
                          } catch (err) {
                            captureException(err);
                            toast.error(`Kon bestand niet opladen: ${getDisplayError(err)}`);
                          }
                        }}
                        onCancel={() => setUploadType(null)}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <DocumentDialogFooter>
            <Dialog.Close asChild>
              <BaseButton size={8}>Sluit</BaseButton>
            </Dialog.Close>
          </DocumentDialogFooter>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
